import { useMemo, useRef, useCallback, useState } from 'react';

import classNames from 'classnames';
import { useAtom } from 'jotai';
import { ReactTable, Checkbox, Select, Tooltip, SkeletonLine } from '@crazyegginc/hatch';

import { useSelection, usePermissions } from '/src/hooks';
import { formatCurrency } from '/src/utils';
import { LazyImg } from '/src/components/LazyImg';
import { WebP } from '/src/components/WebP';
import { goalDetailSelectedTriggerType } from '/src/features/goals/store';
import { combineTriggersWithSegments, resolveTriggerKind } from '/src/features/goals/utils';
import { RenderTriggerText } from '/src/features/goals/components/RenderTriggerText';

import { SORT_ORDER_TYPES, FEATURES } from '/src/features/_global/constants';
import {
  MAXIMUM_SELECTION,
  COLOR_MATRIX,
  TRIGGER_CHART_TYPES,
  FAKE_DELETED_TRIGGERS_ID,
  GOAL_TRIGGER_TYPES,
} from '/src/features/goals/constants';
import { getGoalTriggerMetadata } from '/src/features/goals/triggerMetadata';
import { AppLink } from '/src/features/_global/components/AppLink';

function VariantColor({ index }) {
  const barClasses = 'absolute bottom-px top-0 w-[5px] pb-px left-0';
  if (index === -1) {
    return <div className={barClasses}>&nbsp;</div>;
  }

  const color = COLOR_MATRIX[index]?.tw ?? 'bg-[#a5acbc]';

  return <div className={classNames(barClasses, color)}>&nbsp;</div>;
}

function GoalTriggersTable({ triggers, goalId, hasValueSet, isFetchingCounts }) {
  const tableRef = useRef(null);
  const [order, setOrder] = useState({
    countType: '',
    sort: SORT_ORDER_TYPES.DESC,
  });

  const sortedTriggers = useMemo(
    () =>
      [...triggers].sort((a, b) => {
        const aValue = Number(a.counts[order.countType]?.displayWorth ?? a.counts[order.countType] ?? 0);
        const bValue = Number(b.counts[order.countType]?.displayWorth ?? b.counts[order.countType] ?? 0);
        if (order.countType) {
          return order.sort === SORT_ORDER_TYPES.DESC ? bValue - aValue : aValue - bValue;
        }
      }),
    [triggers, order.countType, order.sort],
  );

  const currency = triggers[0].counts?.totalValue?.displayCurrency ?? 'USD';
  const [type, setType] = useAtom(goalDetailSelectedTriggerType);
  const { currentSelection, setSelection, clearSelection, toggleSelection } = useSelection();

  const maxSelectable = useMemo(() => Math.min(MAXIMUM_SELECTION, sortedTriggers.length), [sortedTriggers.length]);
  const canEditGoals = usePermissions().can('edit', FEATURES.GOALS).allowed;

  const columns = useMemo(
    () => [
      {
        header: () => (
          <div>
            <Checkbox
              checked={currentSelection.length && currentSelection.length === maxSelectable ? true : false}
              onChange={() => {
                currentSelection.length === maxSelectable
                  ? clearSelection()
                  : setSelection([...sortedTriggers.slice(0, maxSelectable)]);
              }}
              indeterminate={currentSelection.length > 0 && currentSelection.length < maxSelectable}
              title="Toggle all rows selected"
              size="lg"
            />
          </div>
        ),
        cell: ({ row }) => {
          const isSelected = currentSelection.some((x) => x.id === row.original.id);
          const isMaxSelected = currentSelection.length >= MAXIMUM_SELECTION && !isSelected;

          return (
            <>
              <Tooltip
                show={isMaxSelected}
                interactive={true}
                darkTheme={true}
                placement="bottom"
                tooltipContent={`You can only select up to ${MAXIMUM_SELECTION} conversion triggers.`}
              >
                <div>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleSelection(row.original)}
                    size="lg"
                    disabled={isMaxSelected}
                    checkBoxClass={classNames({ '!bg-mystic-500 !opacity-30': isMaxSelected })}
                  />
                </div>
              </Tooltip>
              <VariantColor index={isSelected ? row.original.position : -1} />
            </>
          );
        },
        enableSorting: false,
        id: 'selection',
        size: 60,
        meta: {
          align: 'center',
        },
      },
      {
        header: () => (
          <div className="mr-5 flex items-center">
            Conversion triggers{' '}
            <div className=" ml-[30px]">
              <Select
                options={[
                  { label: 'Graph each trigger separately', value: TRIGGER_CHART_TYPES.SEPARATE },
                  { label: 'Combine selected triggers into one graph', value: TRIGGER_CHART_TYPES.COMBINE },
                ]}
                value={type}
                onChange={(value) => setType(value)}
                aria-label="triggers chart type"
                style={{ width: '340px' }}
                popperClassName="!normal-case"
              />
            </div>
          </div>
        ),
        id: 'goalTriggersName',
        enableSorting: false,
        size: 500,
        meta: {
          align: 'center',
          justify: 'left',
        },
        cell: function SnapshotVisitsCell({ row }) {
          if (row.original.id === FAKE_DELETED_TRIGGERS_ID) {
            return <div className="text-body-3 italic">Previously deleted trigger(s)</div>;
          }
          return <SingleTrigger triggerAndSegments={row.original} isTable={true} />;
        },
      },
      {
        header: () => (
          <div className="text-right leading-[14px] w-full">
            Total
            <br />
            conversions
          </div>
        ),
        accessorKey: 'conversionsCount',
        meta: {
          justify: 'center',
          align: 'right',
        },
        cell: ({ row }) => {
          return <span className="text-right w-full">{row?.original?.counts?.conversionsCount ?? 0}</span>;
        },
      },
      {
        header: () => (
          <div className="text-right leading-[14px] w-full">
            Visitors
            <br />
            Converting
          </div>
        ),
        accessorKey: 'uniqueConversionsCount',
        meta: {
          align: 'center',
          justify: 'right',
        },
        cell: function MemberCell({ row }) {
          return <span className="text-right w-full">{row?.original?.counts?.uniqueConversionsCount ?? 0}</span>;
        },
      },
      {
        header: () => (
          <div className="text-right leading-[14px] w-full">
            Total conversion
            <br />
            Value {hasValueSet && `(${currency})`}
          </div>
        ),
        accessorKey: 'totalValue',
        meta: {
          align: 'center',
          justify: 'center',
        },
        cell: function MemberCell({ row }) {
          const { valueType, type, id, counts } = row.original;
          const triggerHasValueSet =
            !!valueType ||
            id === FAKE_DELETED_TRIGGERS_ID ||
            [GOAL_TRIGGER_TYPES.ECOMMERCE_EVENT, GOAL_TRIGGER_TYPES.API].includes(type);
          const triggerValue = counts?.totalValue?.displayWorth ?? 0;

          if (triggerHasValueSet || triggerValue > 0) {
            return (
              <span className="text-right w-full">
                {formatCurrency(triggerValue, currency, { precision: 10 ** 4 })}
              </span>
            );
          }

          if (!canEditGoals) {
            return <span className="text-body-3 text-right w-full">Not set</span>;
          }

          return (
            <AppLink to={`/goals/${goalId}/edit-value`} className="text-link text-right w-full">
              Set value
            </AppLink>
          );
        },
      },
    ],
    [
      type,
      setType,
      clearSelection,
      currentSelection,
      setSelection,
      toggleSelection,
      currency,
      goalId,
      hasValueSet,
      canEditGoals,
      maxSelectable,
      sortedTriggers,
    ],
  );

  const tableSortData = useMemo(() => {
    return {
      id: order.countType,
      desc: order.sort === SORT_ORDER_TYPES.DESC,
    };
  }, [order.countType, order.sort]);

  const onFetchData = useCallback(
    ({ sorting }) => {
      if (sorting) {
        setOrder({
          countType: sorting[0].id,
          sort: sorting[0].desc ? SORT_ORDER_TYPES.DESC : SORT_ORDER_TYPES.ASC,
        });
      }
    },
    [setOrder],
  );

  return (
    <ReactTable
      columns={columns}
      data={sortedTriggers || []}
      ref={{ tableRef }}
      onFetchData={onFetchData}
      sorting={tableSortData}
      rowHeight={70}
      fetching={isFetchingCounts}
      className="!border-none !rounded-none"
      rowCustomBackground={(row) =>
        classNames({
          'bg-white': row,
        })
      }
      rowCustomClassname={() =>
        classNames({
          'after:!border-solid': true,
          '!pr-10': true,
        })
      }
      headerCustomClassname="!pr-10"
    />
  );
}

function SingleTrigger({ triggerAndSegments, isTable = false }) {
  const trigger = isTable ? triggerAndSegments : triggerAndSegments[0];
  const triggerMetadata = getGoalTriggerMetadata(resolveTriggerKind(trigger));
  const previewSrc = trigger?.preview?.thumbnail ?? null;
  const canShowEditorPreview = !!previewSrc;
  const isDeleted = !!trigger.deletedAt && trigger.deletedAt > 0;

  return (
    <div className={classNames('flex w-full items-center', { 'h-[70px] pl-[26px]': !isTable })}>
      {!canShowEditorPreview ? (
        <div className="h-10 w-10 rounded bg-solitude-500 flex items-center justify-center">
          <WebP
            className="w-[25px]"
            webp={triggerMetadata?.miniWebp ?? triggerMetadata.webp}
            fallback={triggerMetadata?.miniPng ?? triggerMetadata.png}
            width="25"
            alt="trigger type"
          />
        </div>
      ) : (
        <Tooltip
          interactive={true}
          darkTheme={false}
          placement="bottom"
          tooltipContent={
            <LazyImg
              className="h-[88px] w-[138px] !rounded-none"
              alt="goal thumbnail"
              src={previewSrc}
              width="138px"
              height="88px"
            />
          }
        >
          <div className="h-10 w-10 rounded bg-solitude-500 flex items-center justify-center">
            <WebP
              className="w-[25px]"
              webp={triggerMetadata?.miniWebp ?? triggerMetadata.webp}
              fallback={triggerMetadata?.miniPng ?? triggerMetadata.png}
              width="25"
              alt="trigger type"
            />
          </div>
        </Tooltip>
      )}

      <div className="flex flex-col text-left ml-5">
        <RenderTriggerText trigger={trigger} theme="light" single={true} isDeleted={isDeleted} />
      </div>
    </div>
  );
}

export function DetailCardTriggers({ goal, triggersCounts, hasValueSet, isFetchingCounts }) {
  const visibleTriggers = combineTriggersWithSegments(goal, triggersCounts);

  if (visibleTriggers.length <= 0) return null;

  if (visibleTriggers.length > 1) {
    return (
      <div data-testid="goal-triggers-table" className="w-full">
        <GoalTriggersTable
          triggers={visibleTriggers}
          goalId={goal.id}
          hasValueSet={hasValueSet}
          isFetchingCounts={isFetchingCounts}
        />
      </div>
    );
  }

  return isFetchingCounts ? (
    <div className="w-full">
      <SkeletonLine className="!h-[37px] !rounded-sm" width="100%" />
    </div>
  ) : (
    <div data-testid="goal-single-trigger" className="w-full">
      <SingleTrigger triggerAndSegments={visibleTriggers} />
    </div>
  );
}
